import { cloneDeep } from "lodash-es";
import { IPlaceMediaItem } from "../../../main";
import { LazyPlaceMediaItem, PlaceMediaItem } from "../../../models";
import { DataProviderArgs } from "../../DataStore/DataProvider";
import { IPlaceMediaTable } from "../IPlaceMediaTable";
import { DataStoreTable } from "./DataStoreTable";
import { omit } from "lodash-es";
import { createMissingThumbnails } from "../../Media";
import { getModuleLog } from "../../Logger";

export function awsMediaInfoToSimpleMediaInfo(media: PlaceMediaItem | LazyPlaceMediaItem) {
    return {
        id: media.id,
        owner: media.owner,
        groupAccess: media.groupAccess,
        media: cloneDeep(media.media),
        placeMediaId: media.placeMediaId,
        createdAt: media.createdAt,
    } as IPlaceMediaItem;
}

export function simpleMediaInfoToAwsMediaInfo(media: IPlaceMediaItem) {
    return new PlaceMediaItem(omit(media, ["createdAt"]));
}

export class PlaceMediaTable extends DataStoreTable<IPlaceMediaItem> implements IPlaceMediaTable {
    constructor(args: DataProviderArgs) {
        super(
            args,
            "place-media",
            PlaceMediaItem,
            (obj: PlaceMediaItem) => awsMediaInfoToSimpleMediaInfo(obj),
            (obj: IPlaceMediaItem) => simpleMediaInfoToAwsMediaInfo(obj)
        );
    }
    public async allByPlaceId(id: string, ..._args: any[]): Promise<IPlaceMediaItem[] | undefined> {
        const all = await this.all();
        return Array.from(all.filter((x: IPlaceMediaItem) => x.placeMediaId === id));
    }

    public async generateThumbnails() {
        const log = getModuleLog("generate-thumbnails");
        const all = Array.from(await this.all());
        log.info(`Checking ${all.length} items for missing thumbnails`);

        let numFixedDates = 0;
        let numBrokenDates = 0;
        let numFixedThumbnails = 0;
        for (const placeMedia of all) {
            if (placeMedia.media) {
                try {
                    const result = await createMissingThumbnails(placeMedia.media);

                    if (result.updated) {
                        numFixedThumbnails++;
                    }
                    // try and fix missing dateAdded
                    if (result.media && !result.media.dateAdded) {
                        numBrokenDates++;

                        const filename = result.media.name;
                        if (filename) {
                            const converters = [
                                this._parseFilenameDate,
                                this._parseFilenameDate2,
                                this._parseFilenameDate3,
                                this._parseFilenameDate4,
                            ];
                            let dateAdded: Date | undefined;
                            for (const converter of converters) {
                                dateAdded = converter.bind(this)(filename);
                                if (dateAdded) {
                                    break;
                                }
                            }

                            if (dateAdded) {
                                result.media.dateAdded = dateAdded.toISOString();
                                result.updated = true;

                                log.info(
                                    `Fixing missing dateAdded for ${placeMedia.id} - filename: ${filename}`
                                );
                                numFixedDates++;
                            } else {
                                log.info(
                                    `Couldn't fix missing dateAdded for ${placeMedia.id} - filename: ${filename}`
                                );
                            }
                        }
                    }

                    if (result.updated) {
                        Object.assign(placeMedia.media, result.media);
                        await this.update(placeMedia);
                        log.info(`Updated thumbnails for ${placeMedia.id}`);
                    }
                } catch (e) {
                    log.error(`Error generating thumbnails for ${placeMedia.id}: ${e}`);
                }
            }
        }
        log.info(`Fixed ${numFixedDates} missing dates out of ${numBrokenDates} broken dates`);
        log.info(`Fixed ${numFixedThumbnails} missing thumbnails`);
    }

    // YYYY-MM-DD HH.mm.ss
    private _parseFilenameDate(filename: string): Date | undefined {
        const regex = /^(\d{4})-(\d{2})-(\d{2}) (\d{2})\.(\d{2})\.(\d{2})/;
        const match = filename.match(regex);
        if (match) {
            const [_, year, month, day, hour, minute, second] = match;
            return new Date(
                parseInt(year),
                parseInt(month) - 1,
                parseInt(day),
                parseInt(hour),
                parseInt(minute),
                parseInt(second)
            );
        }
        return undefined;
    }

    // YYYYMMDD_HHMMSS
    private _isValidDate(
        year: number,
        month: number,
        day: number,
        hours: number,
        minutes: number,
        seconds: number
    ): boolean {
        const date = new Date(year, month - 1, day, hours, minutes, seconds);

        return (
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day &&
            date.getHours() === hours &&
            date.getMinutes() === minutes &&
            date.getSeconds() === seconds
        );
    }

    private _parseFilenameDate2(dateString: string): Date | undefined {
        const regex = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/;
        const match = dateString.match(regex);

        if (!match) {
            return undefined;
        }

        const [, yearStr, monthStr, dayStr, hoursStr, minutesStr, secondsStr] = match;
        const year = Number(yearStr);
        const month = Number(monthStr);
        const day = Number(dayStr);
        const hours = Number(hoursStr);
        const minutes = Number(minutesStr);
        const seconds = Number(secondsStr);

        if (!this._isValidDate(year, month, day, hours, minutes, seconds)) {
            return undefined;
        }

        return new Date(year, month - 1, day, hours, minutes, seconds);
    }

    // PXL_20230623_045555606.jpg
    private _parseFilenameDate3(dateString: string): Date | undefined {
        if (dateString.startsWith("PXL_")) {
            return this._parseFilenameDate2(dateString.substring(4));
        }
        return undefined;
    }

    private _parseFilenameDate4(dateString: string): Date | undefined {
        if (dateString.startsWith("IMG_")) {
            return this._parseFilenameDate2(dateString.substring(4));
        }
        return undefined;
    }
}
